/**
 * SE-6490 — tag state for the pipeline card's tags row.
 *
 * @remarks
 * Owns filtering, the optimistic local removal set, and the split between the
 * chips that render and the `+N` overflow the design collapses the rest into.
 * The row itself is presentational (`PlayerCardTagsView`).
 * @module components/organisms/custom/ui/PlayerCardTags/PlayerCardTagsContainer
 */
import React, { useCallback, useMemo, useState } from "react";

import PlayerCardTagsView from "./PlayerCardTagsView";

import { filterUnwantedTags, logFilteredTags } from "@/utils/tagFiltering";

/**
 * Tag chips shown before the rest collapse into `+N`.
 * @remarks
 * Two, matching the design (which shows two chips and a `+2` on a four-tag
 * card) and the watchlist pills on the same Figma component.
 */
const MAX_VISIBLE_TAGS = 2;

/** Props for PlayerCardTagsContainer */
interface PlayerCardTagsProps {
  /** All of the player's tags, with board-scoped colours from GraphQL */
  readonly displayedTags: { name: string; color: string }[];
  /** Player id — used only to attribute the tag-filtering log */
  readonly playerId: string;
  /** Removal handler, called with the tag name */
  readonly onRemoveTag?: (tagName: string) => void;
  /** Suppresses a drag starting from a tag interaction */
  readonly onPreventDrag?: () => void;
  /** Whether the viewer may remove tags */
  readonly showRemoveButtons?: boolean;
}

const PlayerCardTagsContainer = React.memo(
  ({
    displayedTags,
    playerId,
    onRemoveTag,
    onPreventDrag,
    showRemoveButtons = false,
  }: PlayerCardTagsProps) => {
    // Local optimistic removals, so a removed chip disappears immediately
    // rather than waiting for the mutation to round-trip.
    const [localRemovedTags, setLocalRemovedTags] = useState<Set<string>>(
      new Set()
    );

    const playerTagsData = useMemo(() => {
      const filtered = filterUnwantedTags(displayedTags);
      logFilteredTags(displayedTags, filtered, playerId);
      return filtered;
    }, [displayedTags, playerId]);

    const playerTags = useMemo(
      () =>
        (playerTagsData ?? []).filter(tag => !localRemovedTags.has(tag.name)),
      [playerTagsData, localRemovedTags]
    );

    /** The chips that render, and the count the `+N` stands for. */
    const { visibleTags, overflowCount } = useMemo(
      () => ({
        visibleTags: playerTags.slice(0, MAX_VISIBLE_TAGS),
        overflowCount: Math.max(0, playerTags.length - MAX_VISIBLE_TAGS),
      }),
      [playerTags]
    );

    const handleRemoveTag = useCallback(
      (tagName: string) => {
        setLocalRemovedTags(prev => new Set([...prev, tagName]));
        onRemoveTag?.(tagName);
      },
      [onRemoveTag]
    );

    return (
      <PlayerCardTagsView
        playerTags={visibleTags}
        overflowCount={overflowCount}
        showRemoveButtons={showRemoveButtons}
        onRemoveTag={handleRemoveTag}
        onPreventDrag={onPreventDrag}
      />
    );
  }
);

PlayerCardTagsContainer.displayName = "PlayerCardTagsContainer";

export default PlayerCardTagsContainer;
